import React from 'react';
import { Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { findIconDefinition, IconName } from '@fortawesome/fontawesome-svg-core';
import {
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    closestCenter,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useAppDispatch, useAppSelector } from '../mapSlice';
import { RootState } from '../../../store';
import {
    BaseLayerState,
    toggleBaseLayer,
    setBaseLayerOpacity,
    reorderBaseLayers,
} from '../mapUiSlice';
import { getBaseLayer } from '../../../config/mapLayers';
import './LayersPanel.scss';

const icon = (name: string) => findIconDefinition({ prefix: 'fas', iconName: name as IconName });

/** A single draggable layer row. */
function SortableLayerRow({ layer }: { layer: BaseLayerState }) {
    const dispatch = useAppDispatch();
    const def = getBaseLayer(layer.id);
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({ id: layer.id });

    if (!def) return null;

    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
        zIndex: isDragging ? 2 : undefined,
    };
    const opacityPct = Math.round(layer.opacity * 100);

    return (
        <div className="layer-row" ref={setNodeRef} style={style}>
            <div className="layer-header">
                <button
                    type="button"
                    className="drag-handle"
                    aria-label={`Reorder ${def.label}`}
                    {...attributes}
                    {...listeners}
                >
                    <FontAwesomeIcon icon={icon('grip-vertical')} />
                </button>
                <Form.Check
                    type="switch"
                    id={`layer-toggle-${layer.id}`}
                    className="layer-toggle"
                    checked={layer.visible}
                    onChange={() => dispatch(toggleBaseLayer(layer.id))}
                    label={
                        <span className="layer-label">
                            <FontAwesomeIcon icon={icon(def.icon)} /> {def.label}
                        </span>
                    }
                />
            </div>
            <div className="layer-opacity">
                <Form.Range
                    min={0}
                    max={100}
                    value={opacityPct}
                    disabled={!layer.visible}
                    aria-label={`${def.label} opacity`}
                    onChange={(e) =>
                        dispatch(
                            setBaseLayerOpacity({
                                id: layer.id,
                                opacity: Number(e.target.value) / 100,
                            })
                        )
                    }
                />
                <span className="opacity-value">{opacityPct}%</span>
            </div>
        </div>
    );
}

/**
 * Layer manager shown under the ToolBox "Layers" button. Lists every base
 * layer top-to-bottom (top of the list is drawn on top of the map) and lets
 * the operator toggle each layer, blend it with an opacity slider, and
 * drag-reorder the stack via the handle on the left.
 */
export default function LayersPanel() {
    const dispatch = useAppDispatch();
    const layers = useAppSelector((state: RootState) => state.mapUi.baseLayers);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
    );

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        dispatch(
            reorderBaseLayers({ activeId: String(active.id), overId: String(over.id) })
        );
    };

    return (
        <div className="layers-panel">
            <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
            >
                <SortableContext
                    items={layers.map((l) => l.id)}
                    strategy={verticalListSortingStrategy}
                >
                    {layers.map((layer) => (
                        <SortableLayerRow key={layer.id} layer={layer} />
                    ))}
                </SortableContext>
            </DndContext>
        </div>
    );
}
